import { assertValidProjectDocument, deriveProjectTimeline, type JsonValue, type ProjectDocument } from "@hyperframes/react/browser";
import { DEFAULT_CANVAS_BACKGROUND, DEFAULT_CARD_DURATION_MS, MAX_CARD_DURATION_MS, MIN_CARD_DURATION_MS, type CarouselVariant, type ExperienceVariant, type ImportedShop, type ProductSelection } from "./contracts.js";
import { DEFAULT_EXPORT_FORMAT, getExportFormat, type ExportFormat } from "./export-formats.js";

export type ShopVideoDraft = {
  project: ProjectDocument;
  duration: number;
};

export type VideoHeadlineOptions = {
  headline?: string;
  eyebrow?: string;
  showHeading?: boolean;
  color?: string;
  eyebrowColor?: string;
};

function slug(value: string): string {
  const normalized = value.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
  return normalized || "etsy-shop";
}

function displayTitle(value: string): string {
  const title = value.split("—")[0]!.trim();
  return title.length <= 28 ? title : `${title.slice(0, 27).trimEnd()}…`;
}

export function createShopVideoProject(shop: ImportedShop, selection: ProductSelection, carouselVariant: CarouselVariant = "curved-focus-carousel", exportFormat: ExportFormat = DEFAULT_EXPORT_FORMAT, experience: ExperienceVariant = "etsy", cardDurationMs = DEFAULT_CARD_DURATION_MS, canvasBackground = DEFAULT_CANVAS_BACKGROUND, videoHeadline: VideoHeadlineOptions = {}): ShopVideoDraft {
  const format = getExportFormat(exportFormat);
  const layout = format.carousel[carouselVariant];
  const secondsPerCard = Math.max(MIN_CARD_DURATION_MS, Math.min(MAX_CARD_DURATION_MS, cardDurationMs)) / 1_000;
  const carouselDuration = (selection.selected.length + 1) * secondsPerCard;
  const projectId = `${slug(shop.handle)}-catalogue-video`;
  const catalogueId = `${projectId}-catalogue`;
  const cards: JsonValue = selection.selected.map(({ product, cardLayout, imageTransform }) => ({
    image: product.primaryImage,
    title: displayTitle(product.title),
    subtitle: product.price,
    layout: cardLayout ?? "overlay",
    imageScale: imageTransform?.zoom ?? 1,
    imageX: imageTransform?.offsetX ?? 0,
    imageY: imageTransform?.offsetY ?? 0,
  }));

  const project: ProjectDocument = {
    schemaVersion: "1.0",
    id: projectId,
    title: `${shop.name} catalogue video`,
    format: { width: format.width, height: format.height, fps: format.fps },
    timing: { mode: "auto" },
    theme: {
      palette: {
        background: canvasBackground,
        surface: "#161c29",
        foreground: videoHeadline.color ?? "#fffaf6",
        muted: "#a8afbc",
        accent: "#ff6577",
        accentAlt: "#85d5ff",
      },
      typography: { displayFamily: "Inter", bodyFamily: "Inter", scale: "large" },
      density: "spacious",
      radius: "large",
      motion: { intensity: "expressive", easing: "snappy" },
    },
    scenes: [
      {
        id: catalogueId,
        family: "camera-journey",
        variant: carouselVariant,
        duration: carouselDuration,
        content: {
          eyebrow: videoHeadline.eyebrow ?? (experience === "meta" ? "SELECTED PRODUCTS" : "SELECTED FROM THE SHOP"),
          eyebrowColor: videoHeadline.eyebrowColor ?? "#ff6577",
          headline: videoHeadline.headline ?? shop.title ?? "Discover the collection",
          cards,
          cardDuration: secondsPerCard,
          activeScale: 1,
          inactiveScale: layout.inactiveScale,
          blur: 7,
          cardWidth: layout.cardWidth,
          cardHeight: layout.cardHeight,
          gap: layout.gap,
          safeTop: format.safeArea.top * 100,
          safeRight: format.safeArea.right * 100,
          safeBottom: format.safeArea.bottom * 100,
          safeLeft: format.safeArea.left * 100,
          showHeading: videoHeadline.showHeading ?? true,
          returnToStart: true,
          ...(carouselVariant === "curved-focus-carousel" ? { curveDepth: layout.curveDepth, curveTilt: layout.curveTilt } : {}),
        },
      },
    ],
    transitions: [],
  };
  assertValidProjectDocument(project);
  return { project, duration: deriveProjectTimeline(project).duration };
}
